/**
 * Vista admin de solo lectura sobre la tabla `otp_codes`. Listado paginado
 * con filtros (origen, estado, validez, búsqueda) — útil cuando el email con
 * el OTP no llega al destinatario y el operador necesita inspeccionar el
 * código generado para completar el flujo (registro, solicitud de adelanto).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Input } from '@/components/ui/input';

type OtpSource = 'auth' | 'advance_request';

interface OtpListItem {
  id: string;
  userAccountId: string;
  keycloakId: string | null;
  companyId: string | null;
  code: string;
  source: OtpSource;
  verified: boolean;
  expiresAt: string;
  isExpired: boolean;
  createdAt: string;
  updatedAt: string;
}

interface OtpsListResult {
  page: number;
  pageSize: number;
  total: number;
  items: OtpListItem[];
}

const REFRESH_INTERVAL_MS = 15000;
const PAGE_SIZE = 50;

const SOURCE_LABEL: Record<OtpSource, string> = {
  auth: 'Auth',
  advance_request: 'Adelanto',
};

const fmtDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/Santiago',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const relativeFromNow = (iso: string) => {
  const target = new Date(iso).getTime();
  const diffMs = target - Date.now();
  const sign = diffMs >= 0 ? '+' : '−';
  const absSec = Math.floor(Math.abs(diffMs) / 1000);
  if (absSec < 60) return `${sign}${absSec}s`;
  const min = Math.floor(absSec / 60);
  if (min < 60) return `${sign}${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${sign}${hr}h`;
  const day = Math.floor(hr / 24);
  return `${sign}${day}d`;
};

export default function OtpsPage() {
  const [source, setSource] = useState<OtpSource | 'all'>('all');
  const [verified, setVerified] = useState<'all' | 'verified' | 'unused'>('all');
  const [validity, setValidity] = useState<'all' | 'active' | 'expired'>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<OtpsListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce búsqueda
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (source !== 'all') params.set('source', source);
      if (verified !== 'all') params.set('verified', verified);
      if (validity !== 'all') params.set('validity', validity);
      if (search) params.set('search', search);
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      const url = `${getAdminEndpointUrl('otps', 'list')}?${params.toString()}`;
      const result = await adminHttpClient.get<OtpsListResult>(url);
      setData(result);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error cargando OTPs:', err);
    } finally {
      setLoading(false);
    }
  }, [source, verified, validity, search, page]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchList, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchList]);

  function copyCode(item: OtpListItem) {
    navigator.clipboard.writeText(item.code).catch(() => undefined);
    setCopiedId(item.id);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopiedId(null), 1500);
  }

  const totalPages = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Math.ceil(data.total / data.pageSize));
  }, [data]);

  return (
    <AdminLayout
      title="OTPs"
      subtitle="Códigos OTP generados — listado de solo lectura con filtros y búsqueda."
      actions={
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
            últ. refresh{' '}
            <span className="text-slate-700 dark:text-slate-300">
              {lastRefresh.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'America/Santiago' })}
            </span>
          </span>
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-all ${
              autoRefresh
                ? 'bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-300/50 dark:bg-emerald-950/40 dark:text-emerald-400 dark:ring-emerald-900/50'
                : 'border border-slate-200 bg-white text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400'
            }`}
          >
            <span className="relative flex h-1.5 w-1.5">
              {autoRefresh && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />}
              <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${autoRefresh ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            </span>
            {autoRefresh ? 'Live · 15s' : 'Pausado'}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Filtros */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 flex flex-wrap items-center gap-3">
          <SegmentedFilter
            label="Origen"
            value={source}
            options={[
              { value: 'all', label: 'Todos' },
              { value: 'auth', label: 'Auth' },
              { value: 'advance_request', label: 'Adelanto' },
            ]}
            onChange={(v) => {
              setSource(v as OtpSource | 'all');
              setPage(1);
            }}
          />
          <SegmentedFilter
            label="Estado"
            value={verified}
            options={[
              { value: 'all', label: 'Todos' },
              { value: 'unused', label: 'Sin usar' },
              { value: 'verified', label: 'Usados' },
            ]}
            onChange={(v) => {
              setVerified(v as 'all' | 'verified' | 'unused');
              setPage(1);
            }}
          />
          <SegmentedFilter
            label="Validez"
            value={validity}
            options={[
              { value: 'all', label: 'Todos' },
              { value: 'active', label: 'Vigentes' },
              { value: 'expired', label: 'Expirados' },
            ]}
            onChange={(v) => {
              setValidity(v as 'all' | 'active' | 'expired');
              setPage(1);
            }}
          />
          <div className="flex-1 min-w-[220px]">
            <Input
              placeholder="Buscar por código, id, userAccountId o keycloakId…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
              className="h-9"
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Código</th>
                  <th className="px-4 py-3">Origen</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Expira</th>
                  <th className="px-4 py-3">Generado</th>
                  <th className="px-4 py-3">Usuario</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {loading && !data ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                      <span className="material-symbols-outlined text-3xl text-blue-500 animate-spin">progress_activity</span>
                    </td>
                  </tr>
                ) : !data || data.items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 mx-auto flex items-center justify-center mb-3">
                        <span className="material-symbols-outlined text-3xl text-slate-400">pin</span>
                      </div>
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-400">No hay OTPs que coincidan con el filtro.</p>
                    </td>
                  </tr>
                ) : (
                  data.items.map((o) => {
                    const expiredOrUsed = o.verified || o.isExpired;
                    return (
                      <tr key={o.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-900/60">
                        <td className="px-4 py-3 align-top">
                          <button
                            type="button"
                            onClick={() => copyCode(o)}
                            title="Copiar código"
                            className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1 font-mono text-base font-semibold tracking-[0.2em] transition-colors ${
                              expiredOrUsed
                                ? 'border-slate-200 bg-slate-50 text-slate-400 line-through dark:border-slate-800 dark:bg-slate-900/40'
                                : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-300'
                            }`}
                          >
                            {o.code}
                            <span className="material-symbols-outlined text-[14px] opacity-70">{copiedId === o.id ? 'check' : 'content_copy'}</span>
                          </button>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span className="inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                            {SOURCE_LABEL[o.source]}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top">
                          {o.verified ? (
                            <Badge tone="emerald" icon="check_circle">
                              Usado
                            </Badge>
                          ) : o.isExpired ? (
                            <Badge tone="rose" icon="schedule">
                              Expirado
                            </Badge>
                          ) : (
                            <Badge tone="amber" icon="hourglass_top">
                              Vigente
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="text-[12px] text-slate-700 dark:text-slate-300">{fmtDateTime(o.expiresAt)}</div>
                          <div className="text-[11px] font-mono text-slate-400">{relativeFromNow(o.expiresAt)}</div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="text-[12px] text-slate-700 dark:text-slate-300">{fmtDateTime(o.createdAt)}</div>
                          <div className="text-[11px] font-mono text-slate-400">{relativeFromNow(o.createdAt)}</div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="text-[11px] font-mono text-slate-500" title="userAccountId">
                            uid · <span className="text-slate-700 dark:text-slate-300">{o.userAccountId}</span>
                          </div>
                          {o.keycloakId && (
                            <div className="text-[11px] font-mono text-slate-500 break-all" title="keycloakId">
                              kc · <span className="text-slate-700 dark:text-slate-300">{o.keycloakId}</span>
                            </div>
                          )}
                          {o.companyId && (
                            <div className="text-[11px] font-mono text-slate-500" title="companyId">
                              co · <span className="text-slate-700 dark:text-slate-300">{o.companyId}</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer paginación */}
          {data && data.total > 0 && (
            <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 px-4 py-2.5 text-xs text-slate-500">
              <div>
                Mostrando <strong className="text-slate-700 dark:text-slate-300">{(page - 1) * PAGE_SIZE + 1}</strong>–
                <strong className="text-slate-700 dark:text-slate-300">{Math.min(page * PAGE_SIZE, data.total)}</strong> de{' '}
                <strong className="text-slate-700 dark:text-slate-300">{data.total}</strong>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1 text-slate-600 dark:text-slate-300 disabled:opacity-40"
                >
                  ‹ Anterior
                </button>
                <span className="font-mono text-[11px]">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1 text-slate-600 dark:text-slate-300 disabled:opacity-40"
                >
                  Siguiente ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function SegmentedFilter<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <div className="inline-flex items-center rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-0.5">
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                active
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Badge({ tone, icon, children }: { tone: 'emerald' | 'rose' | 'amber'; icon: string; children: React.ReactNode }) {
  const map: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/50',
    rose: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900/50',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900/50',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${map[tone]}`}>
      <span className="material-symbols-outlined text-[14px]">{icon}</span>
      {children}
    </span>
  );
}
